import { Injectable } from '@angular/core';
import { ChapterModel } from './models/chapter.model';
import { MediaModel } from './models/media.model';
import { Howl } from 'howler';
import { UploadFile, UploadInput } from 'ngx-uploader';
import { HumanInterface } from '../engine/models/human.interface';

declare const Human: HumanInterface;

const postAudioRoute = '/ws/media/audio/upload';
const getAudioRoute = '/media/audio';
const legacyBaseUrl = '/content/states';

const audioUUIDExp = new RegExp(`^${getAudioRoute}/(.*)/.*$`);

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  constructor() {}

  private audioPlayers = new Map<string, Howl>();

  createAudioUploadEvent(audioFile: UploadFile): UploadInput {
    return {
      type: 'uploadFile',
      url: postAudioRoute,
      method: 'POST',
      file: audioFile,
    } as UploadInput;
  }

  // Returns key / value pair
  getAudioClip(media: object, lang: string): [string, MediaModel] {
    const audioClipPair = Object.entries(media).find(([clipId, clip]) => {
      return clip.data.streamType === 'audio' && clip.data.lang === lang;
    });

    return audioClipPair || [undefined, undefined];
  }

  // Returns key / value pair
  createAudioClip(chapter: ChapterModel, audioUrl: string, lang: string): [string, MediaModel] {
    const branch = Human.timeline.branchTimelines[chapter.id];
    const clipId = this.getAudioUUID(audioUrl);

    const audioClip = {
      data: {
        clipId: clipId,
        streamType: 'audio',
        alternatives: ['m4a'],
        fullUrl: audioUrl,
        time1: branch.timeRange.firstTime,
        time2: branch.timeRange.lastTime,
        lang: lang,
      },
    } as MediaModel;

    return [clipId, audioClip];
  }

  getAudioClipUrl(media: object, lang: string): string {
    const [clipId, audioClip] = this.getAudioClip(media, lang);
    return audioClip ? audioClip.data.fullUrl : '';
  }

  // Handles legacy audio sources
  getAudioSrc(src: string): string {
    if (!src.match(getAudioRoute)) {
      src = `${legacyBaseUrl}/${src}`;
    }

    return src;
  }

  getAudioPlayer(audioURL: string): Howl {
    return this.audioPlayers.get(audioURL);
  }

  addAudioPlayer(audioURL: string): Howl {
    this.removeAudioPlayer(audioURL);

    audioURL = this.getAudioSrc(audioURL);

    const audioPlayer = new Howl({ src: [audioURL] });
    this.audioPlayers.set(audioURL, audioPlayer);

    return audioPlayer;
  }

  removeAudioPlayer(audioURL: string) {
    const audioPlayer = this.audioPlayers.get(audioURL);

    if (audioPlayer) {
      audioPlayer.unload();
      this.audioPlayers.delete(audioURL);
    }
  }

  stopAudio() {
    this.audioPlayers.forEach((player) => {
      player.stop();
    });
  }

  private getAudioUUID(audioUrl: string): string {
    const match = audioUrl.match(audioUUIDExp);
    return match[1];
  }
}
